import { Link } from 'react-router-dom'
import { useUniversePath } from '../lib/universe'
import { formatPoints } from '../lib/decimal'
import type { DeltaItem, StandingItem } from '../api/types'

/**
 * A championship table, with the dropped-scores rule made visible.
 *
 * Two details carry most of the meaning. `position: 0` is a competitor the
 * rules excluded from the order -- Tyrrell in 1984 -- and showing them as
 * "0th" would be a lie, so they get a badge. And the rounds a driver's best-N
 * discarded are struck through, which is where an abstract rule becomes a
 * thing you can see costing somebody a title.
 */
export function StandingsTable({
  items,
  deltas,
  championship,
  rounds,
}: {
  items: StandingItem[]
  deltas?: DeltaItem[]
  championship: 'drivers' | 'constructors'
  /** When set, the counted and dropped rounds are shown per row. */
  rounds?: boolean
}) {
  const delta = new Map((deltas ?? []).map((entry) => [entry.key, entry]))
  const path = championship === 'drivers' ? 'drivers' : 'constructors'
  const to = useUniversePath()

  return (
    <table className="grid standings">
      <thead>
        <tr>
          <th scope="col" className="num">
            Pos
          </th>
          <th scope="col">{championship === 'drivers' ? 'Driver' : 'Constructor'}</th>
          <th scope="col" className="num">
            Points
          </th>
          <th scope="col" className="num">
            Wins
          </th>
          {delta.size > 0 ? (
            <th scope="col" className="num" title="Change against the official table">
              Change
            </th>
          ) : null}
          {rounds ? <th scope="col">Rounds counted</th> : null}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const change = delta.get(item.key)
          return (
            <tr
              key={item.key}
              className={item.championship_won ? 'standings__champion' : undefined}
            >
              <td className="num">
                {item.position === 0 ? (
                  <span className="badge badge--excluded" title="Excluded from the order">
                    excl
                  </span>
                ) : (
                  item.position
                )}
              </td>
              <td>
                <Link to={to(`/${path}/${item.entity}`)}>{item.name ?? item.entity}</Link>
                {item.engine_manufacturer ? (
                  <span className="muted"> · {item.engine_manufacturer}</span>
                ) : null}
              </td>
              <td className="num">{formatPoints(item.points)}</td>
              <td className="num">{item.wins || ''}</td>
              {delta.size > 0 ? (
                <td className="num">
                  <PositionChange change={change?.position_change ?? null} />
                </td>
              ) : null}
              {rounds ? (
                <td>
                  <RoundBand
                    counted={item.counted_rounds ?? []}
                    dropped={item.dropped_rounds ?? []}
                  />
                </td>
              ) : null}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function PositionChange({ change }: { change: number | null }) {
  if (change === null) return <span className="muted">new</span>
  if (change === 0) return <span className="muted">—</span>
  const better = change > 0
  return (
    <span className={better ? 'change change--up' : 'change change--down'}>
      {better ? '▲' : '▼'}
      {Math.abs(change)}
    </span>
  )
}

/**
 * Counted rounds plain, discarded ones struck through.
 *
 * Comma-separated rather than spaced: a gap between "11" and "12" is not
 * enough to tell them from "1112", and a screen reader gets nothing from CSS.
 */
function RoundBand({ counted, dropped }: { counted: number[]; dropped: number[] }) {
  if (dropped.length === 0 && counted.length === 0) return null
  const all = [...new Set([...counted, ...dropped])].sort((a, b) => a - b)
  const discarded = new Set(dropped)
  return (
    <span className="rounds">
      {all.map((round, index) => (
        <span
          key={round}
          className={discarded.has(round) ? 'rounds__dropped' : 'rounds__counted'}
          title={discarded.has(round) ? `Round ${round} scored but was discarded` : undefined}
        >
          {round}
          {index < all.length - 1 ? <span className="rounds__sep">,</span> : null}
        </span>
      ))}
      {dropped.length > 0 ? <span className="muted"> {dropped.length} discarded</span> : null}
    </span>
  )
}
